import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { useProjectInfo, useProjectUpdate } from "ducks/project/hooks";
import { createLayerItems } from "evergis/core/project/createProjectContent";
import { goToMainBase } from "ducks/location/location";
import { useProjectLayers } from "./useProjectLayers";
import { useDefaultScaleRange } from "../style/useDefaultScaleRange";
import { RoutePanelName } from "../../appConfig/types";
import { ResourceInfoDc } from "../../modules/Catalog/types";

export function useIsLayerInProject(layerName: string): boolean {
  const layers = useProjectLayers();

  return layers.some(layer => layer.name === layerName);
}

export const useAddLayersToCurrentProject = (): ((layers: ResourceInfoDc[]) => void) => {
  const projectMutation = useProjectUpdate();
  const layerItems = useProjectLayers();
  const defaultScaleRange = useDefaultScaleRange();

  return useCallback(
    async (layers: ResourceInfoDc[]) => {
      if (!layers || layers.length === 0) {
        return;
      }

      const uniqueLayers = layers.filter(layer => !layerItems.find(projectItem => projectItem.name === layer.name));

      if (uniqueLayers.length > 0) {
        const newItems = [...createLayerItems(uniqueLayers, defaultScaleRange[0], defaultScaleRange[1]), ...layerItems];

        await projectMutation({
          content: {
            items: newItems,
          },
        });
      }
    },
    [defaultScaleRange, layerItems, projectMutation],
  );
};

export const useCurrentProjectWithAddedLayers = (layers: ResourceInfoDc[]): VoidFunction => {
  const dispatch = useDispatch();
  const [{ name }] = useProjectInfo();
  const addLayers = useAddLayersToCurrentProject();

  return useCallback(() => {
    if (name) {
      addLayers(layers);
      dispatch(goToMainBase({ project: name, panel: RoutePanelName.Layers }));
    }
  }, [layers, name]);
};
